import { useEffect, useRef } from 'react';
import type { FieldErrors, FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

import { SERVER_ERROR_TYPE } from '@/lib/forms';

/** Every field path currently showing a client-side error, nested ones included. */
function clientErrorPaths(errors: FieldErrors, prefix = ''): string[] {
  const paths: string[] = [];

  for (const [key, value] of Object.entries(errors)) {
    if (!value || typeof value !== 'object' || key === 'root') continue;
    const path = prefix ? `${prefix}.${key}` : key;

    if ('type' in value && typeof value.type === 'string') {
      if (value.type !== SERVER_ERROR_TYPE) paths.push(path);
    } else {
      paths.push(...clientErrorPaths(value as FieldErrors, path));
    }
  }

  return paths;
}

/**
 * Makes a form's error messages go away the moment they stop being true.
 *
 * react-hook-form only re-checks the field being edited, and on some forms
 * only once it loses focus. So a message could outlive its cause: "Both
 * passwords must match" stayed under the confirm box after the first box was
 * corrected, and a rule checked on blur kept complaining while the right value
 * was being typed. Here, every change re-checks each field that is currently
 * showing an error — never one that is not, so nothing new is reported early.
 *
 * Call it once beside `useForm`, passing the form it returned.
 */
export function useLiveFormErrors<T extends FieldValues>({
  watch,
  trigger,
  formState,
}: Pick<UseFormReturn<T>, 'watch' | 'trigger' | 'formState'>): void {
  // Read during render so the form re-renders when errors change, and kept in a
  // ref so the subscription below always sees the latest without resubscribing.
  const errors = useRef(formState.errors);
  errors.current = formState.errors;

  useEffect(() => {
    const subscription = watch(() => {
      const showing = clientErrorPaths(errors.current);
      if (showing.length > 0) void trigger(showing as FieldPath<T>[]);
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);
}
